import { localizedString } from 'helpers/localize';
import { DEFAULT_STORY_LOCALE, TStoryLocale } from 'constants/storyLocales';
import {
  TGroupDetail,
  TGroupSummary,
  TLocalizedText,
  TSnapshotMeta,
  TStorySnapshot,
} from 'types/story';
import { TStoryLocalizedItemsWire } from 'types/storyWire';

import { pendingChanges } from './divergence';
import { fromWireItems } from './wire';

export const toWireTitle = (
  title: string,
  translations?: Partial<Record<TStoryLocale, string>>
): TLocalizedText => localizedString(title, translations) as TLocalizedText;

export const fromWireTitle = (
  title: TLocalizedText | undefined
): { title: string; titleTranslations: TLocalizedText } => {
  const { [DEFAULT_STORY_LOCALE]: base, ...rest } = title ?? {};
  return { title: base ?? '', titleTranslations: rest };
};

export const snapshotTitle = (snapshot: TStorySnapshot | null | undefined): string =>
  snapshot?.title?.[DEFAULT_STORY_LOCALE] ?? '';

/** Inbound: locale-grouped wire items, not the editor-shaped ones `TStorySnapshot` exposes. */
type TWireSnapshot = Omit<Partial<TStorySnapshot>, 'items'> & {
  items?: TStoryLocalizedItemsWire;
};

export type TWireGroupDetail = Omit<TGroupDetail, 'liveSnapshot' | 'draftSnapshot'> & {
  liveSnapshot?: TWireSnapshot | null;
  draftSnapshot?: TWireSnapshot | null;
};

const normalizeSnapshot = (snapshot: TWireSnapshot): TStorySnapshot => ({
  id: snapshot.id ?? '',
  contentVersion: snapshot.contentVersion ?? 0,
  version: snapshot.version ?? 0,
  title: snapshot.title ?? {},
  startsAt: snapshot.startsAt ?? null,
  endsAt: snapshot.endsAt ?? null,
  segmentId: snapshot.segmentId ?? null,
  pinned: snapshot.pinned ?? false,
  coverMediaUrl: snapshot.coverMediaUrl ?? {},
  items: fromWireItems(snapshot.items ?? { en: [] }),
  publishedAt: snapshot.publishedAt ?? null,
});

export const normalizeGroupDetail = (detail: TWireGroupDetail): TGroupDetail => ({
  id: detail.id,
  name: detail.name,
  status: detail.status,
  placement: detail.placement,
  displayOrder: detail.displayOrder ?? 0,
  liveSnapshot: detail.liveSnapshot ? normalizeSnapshot(detail.liveSnapshot) : null,
  draftSnapshot: normalizeSnapshot(detail.draftSnapshot ?? {}),
});

const snapshotMeta = (snapshot: TStorySnapshot): TSnapshotMeta => ({
  title: snapshotTitle(snapshot),
  coverMediaUrl: snapshot.coverMediaUrl,
  slideCount: snapshot.items.length,
  segmentId: snapshot.segmentId,
  startsAt: snapshot.startsAt,
  endsAt: snapshot.endsAt,
  pinned: snapshot.pinned,
  publishedAt: snapshot.publishedAt,
});

export const toSummary = (detail: TGroupDetail): TGroupSummary => {
  const source = detail.draftSnapshot ?? detail.liveSnapshot;

  return {
    id: detail.id,
    name: detail.name,
    status: detail.status,
    placement: detail.placement,
    displayOrder: detail.displayOrder,
    title: snapshotTitle(source),
    // The card shows one thumbnail; English is the canonical cover.
    coverMediaUrl: source?.coverMediaUrl?.[DEFAULT_STORY_LOCALE] ?? null,
    slideCount: source?.items.length ?? 0,
    pinned: source?.pinned ?? false,
    hasLive: detail.liveSnapshot !== null,
    segmentId: source?.segmentId ?? null,
    startsAt: source?.startsAt ?? null,
    endsAt: source?.endsAt ?? null,
    publishedAt: detail.liveSnapshot?.publishedAt ?? null,
    liveMeta: detail.liveSnapshot ? snapshotMeta(detail.liveSnapshot) : null,
    pending: pendingChanges(detail),
  };
};
